import { ECrashValidationErrorUiProps } from '../../../data-model/models/c-crash-validation-error.model';

export const buildRuleIdValidationErrorFunctionMap = (): Map<string, (...args: unknown[]) => ECrashValidationErrorUiProps> => {
  const ruleIdValidationErrorFunctionMap = new Map<string, (...args: unknown[]) => ECrashValidationErrorUiProps>();

  ruleIdValidationErrorFunctionMap.set('DirectionFromIntersectionMustMatchApplicabilityOfDistanceFromIntersection', () => ({
    shortDescription: 'Invalid Direction from Intersection',
    longDescription:
      'If the incident occured at an intersection, set direction from intersection to "not applicable". If the incident did not occur at an intersection, set direction from intersection to a valid value.',
    uiPath: ['Overview', 'Location', 'Location', 'Direction from Intersection'],
    keyPath: ['crash', 0, 'location', 0, 'DIRECTION_FROM_INTER'],
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('VehicleUsageDescriptionMustBeNullIfNotRequired', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Vehicle Usage / Vehicle Usage Description',
    longDescription: `You have an invalid combination of vehicle usage and vehicle usage description for vehicle ${vehicleIndex}. Typically, vehicle usage can be set to "unknown" and vehicle usage description can be set to "no special function". If vehicle usage is not set to "unknown", vehicle usage description should most likely be cleared.`,
    uiPath: ['Vehicle', 'Factors', 'Vehicle Factors', 'Vehicle Usage Description'],
    keyPath: ['vehic', 0, 'contribfactors', 0, 'VEH_USAGE_DESC'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  // could probably update via a modal
  ruleIdValidationErrorFunctionMap.set('IntersectionTrafficControlMustBeNotApplicableIfIntersectionApproachIsNo', () => ({
    shortDescription: 'Invalid Intersection Traffic Control',
    longDescription: 'Since the incident did not occur at an intersection, set intersection traffic control to "not applicable".',
    uiPath: ['Overview', 'Location', 'Location', 'Intersection Traffic Control'],
    keyPath: ['crash', 0, 'location', 0, 'INTER_TRAFFIC_CTRL_CD'],
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  // could probably update via a modal
  ruleIdValidationErrorFunctionMap.set('JunctionLocationMustHaveTrafficControlDevicesIfIntersectionTrafficControlIsYes', () => ({
    shortDescription: 'Invalid Junction Location',
    longDescription: 'Since the incident occurred at an intersection, the junction location must be "intersection or related".',
    uiPath: ['Overview', 'Location', 'Roadway', 'Junction Location'],
    keyPath: ['crash', 0, 'location', 0, 'JUCTION_LOCATION_CD'],
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  // could potentially build a ui and key path dynamically, non vehicular property damage questions are not repeatable, there are always four
  ruleIdValidationErrorFunctionMap.set('PropertyTypeDescriptionMustNotBeEmptyIfRequired', (propertyIndex: number) => ({
    shortDescription: 'Invalid Non Vehicular Property Type Description',
    longDescription: `Please provide a damage type description for non vehicular property ${propertyIndex}.`,
    uiPath: null,
    keyPath: null,
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  /**
   * TODO: i think this is a back end issue, i don't think the api will let the client have null values for driver fields
   */
  // ruleIdValidationErrorFunctionMap.set('IsOwnerAddressSameAsDriverMustBeFalseIfDriverAddressIsUnknown', vehicleIndex => ({
  //   shortDescription: 'Invalid Owner Address Same as Driver',
  //   longDescription: `Please set owner address same as driver to false for vehicle ${vehicleIndex} cause the driver address is unknown.`,
  //   uiPath: ['Vehicle', 'Driver', 'Vehicle Owner', 'Is Owner Address Same As Driver?'],
  //   keyPath: ['vehic', 'driver', 'OWNER_ADDR_SAME_AS_DRIVER'],
  //   schemaGroupRepeats: true,
  //   schemaGroupIndex: vehicleIndex,
  //   subGroupRepeats: false,
  //   subGroupIndex: 0,
  // }));

  ruleIdValidationErrorFunctionMap.set('VehiclesMustIncludeAtLeastOneOtherParkedVehicleIfSequenceOfEventsInvolvesParkedVehicle', () => ({
    shortDescription: 'Invalid Sequence of Events / Vehicle Type',
    longDescription:
      'Since one of your vehicles has an event in its sequence of events that involves a parked vehicle, you must include at least one vehicle in your vehicle list with a vehicle type of "parked motor vehicle".',
    uiPath: null,
    keyPath: null,
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('VehiclesMustHaveAtLeastOneSchoolBusIfCrashIsDirectlySchoolBusRelated', () => ({
    shortDescription: 'Invalid School Bus Relation / Vehicle Usage',
    longDescription:
      'You have answered "yes, school bus directly involved" or "yes, school bus indirectly involved" to school bus relation. However, you do not have any vehicles with a vehicle usage of "bus - school (public or private)". Please set vehicle usage to "bus - school (public or private)" and clear vehicle usage description for at least one vehicle or set school bus relation to "no".',
    uiPath: null,
    keyPath: null,
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('VehiclesMustIncludeAtLeastOneOtherMovingVehicleIfSequenceOfEventsInvolvesAnotherMovingVehicle', () => ({
    shortDescription: 'Invalid Sequence of Events / Vehicle Type',
    longDescription:
      'Since one of your vehicles has an event in its sequence of events that involves another moving vehicle, you must include at least one vehicle in your vehicle list with a vehicle type of "motor vehicle in transit".',
    uiPath: null,
    keyPath: null,
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('SequenceOfEventsMustContainOneMostHarmfulEvent', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Most Harmful Event',
    longDescription: `Please update the most harmful event for vehicle ${vehicleIndex}. Set it to one of the events in the sequence of events.`,
    uiPath: ['Vehicle', 'Factors', 'Vehicle Factors', 'Most Harmful Vehicle Event'],
    keyPath: ['vehic', vehicleIndex - 1, 'contribfactors', 0, 'M_HARM_EV_CD'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  /**
   * TODO: come back to this, i don't think we need to go crazy with commerical questions
   */
  // ruleIdValidationErrorFunctionMap.set('MotorCarrierTypeDescriptionMustBeNullIfNotRequired', vehicleIndex => ({
  //   shortDescription: 'Invalid Motor Carrier type Description',
  //   longDescription: `Please clear the motor carrier type description for vehicle ${vehicleIndex}. It is not required.`,
  //   uiPath: ['Vehicle', 'Vehicle', 'Commercial', 'Motor Carrier Type Description'],
  //   keyPath: ['vehic', 'vehic', 'UTB_MTR_CARRIER_TYPE_DESC'],
  //   schemaGroupRepeats: true,
  //   schemaGroupIndex: vehicleIndex,
  //   subGroupRepeats: false,
  //   subGroupIndex: 0,
  // }));

  ruleIdValidationErrorFunctionMap.set('VehiclesWithBusBodyTypeMustHaveBusUsage', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Vehicle Usage / Vehicle Usage Description',
    longDescription: `Since vehicle ${vehicleIndex} has a bus body type, set the vehicle usage to an option specific to bus and clear vehicle usage description.`,
    uiPath: ['Vehicle', 'Factors', 'Vehicle Factors', 'Vehicle Usage'],
    keyPath: ['vehic', vehicleIndex - 1, 'contribfactors', 0, 'VEH_USAGE_CD'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  // could potentially build a ui and key path dynamically, non vehicular property damage questions are not repeatable, there are always four
  ruleIdValidationErrorFunctionMap.set('DamageSeverityMustNotBeEmpty', (propertyIndex: number) => ({
    shortDescription: 'Invalid Damage Severity',
    longDescription: `Please select the damage severity for non vehicular property ${propertyIndex}. It is required.`,
    uiPath: null,
    keyPath: null,
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('TrafficwayDivisionMustBeNotDividedIfTrafficwayTravelDirectionIsOneWay', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Trafficway Division',
    longDescription: `Since vehicle ${vehicleIndex} is traveling in a one way direction, set the trafficway division should be set to "not divided".`,
    uiPath: ['Vehicle', 'Factors', 'Vehicle Factors', 'Trafficway Division'],
    keyPath: ['vehic', vehicleIndex - 1, 'contribfactors', 0, 'TRAFF_DIVISION_CD'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('IntersectionGeometryMustBeNotApplicableIfIntersectionApproachIsNo', () => ({
    shortDescription: 'Invalid Intersection Geometry',
    longDescription: 'Since the incident did not occur at an intersection, set intersection geometry to "not applicable".',
    uiPath: ['Overview', 'Location', 'Location', 'Intersection Geometry'],
    keyPath: ['crash', 0, 'location', 0, 'INTER_GEO_CD'],
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('LicensePlateTypeMustBeUnknownIfTagStateUnknown', (vehicleIndex: number) => ({
    shortDescription: 'Invalid License Plate Type / License Plate State',
    longDescription: `If the license plate state for vehicle ${vehicleIndex} is unknown, set the license plate type to "unknown". If the license plate state is known, set the license plate type to a valid value such as "out-of-state license plate" or "regular louisiana license plate".`,
    uiPath: ['Vehicle', 'Vehicle', 'Vehicle', "Vehicle's Licence Plate Type"],
    keyPath: ['vehic', vehicleIndex - 1, 'vehic', 0, 'PLATE_TYPE'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('OwnerAddressStateMustNotBeEmptyIfApplicable', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Owner State',
    longDescription: `Please select the state for the address of the owner of vehicle ${vehicleIndex}. It is required.`,
    uiPath: ['Vehicle', 'Driver', 'Vehicle Owner', "Vehicle Owner's Address"],
    keyPath: ['vehic', vehicleIndex - 1, 'driver', 0, 'OWNER_STATE'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('TravelDirectionMustBeNotOnRoadwayIfVehicleIsParked', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Direction Traveling Before Crash',
    longDescription: `Since vehicle ${vehicleIndex} was parked, the direction traveling before crash should be set to "not on roadway" or "in roadway but not in motion".`,
    uiPath: ['Vehicle', 'Factors', 'Vehicle Factors', 'Direction Traveling Before Crash'],
    keyPath: ['vehic', vehicleIndex - 1, 'contribfactors', 0, 'TRAVEL_DIR'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('IntersectionTrafficControlMustNotBeNotApplicableIfIntersectionApproachIsNotNo', () => ({
    shortDescription: 'Invalid Intersection Traffic Control',
    longDescription:
      'Since intersection approach is "two", "three", "four", or "five", intersection traffic control must be a value OTHER than "not applicable".',
    uiPath: ['Overview', 'Location', 'Location', 'Intersection Traffic Control'],
    keyPath: ['crash', 0, 'location', 0, 'INTER_TRAFFIC_CTRL_CD'],
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('HighwayMustNotBeEmpty', () => ({
    shortDescription: 'Invalid Highway Code',
    longDescription:
      'Please select a valid highway code. Make sure the road classification and highway code match. if road classification is "state highway", the highway code should start with "la", it cannot start with "interstate"',
    uiPath: ['Overview', 'Location', 'Roadway', 'Highway Code'],
    keyPath: ['crash', 0, 'location', 0, 'HWY_TYPE_CD'],
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('DriverLicenseTypeMustBeCommercialIfDriverLicenseClassIsAlwaysCommercial', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Driver License Type',
    longDescription: `Since the driver license class for vehicle ${vehicleIndex} is a commercial drivers license class, set the drivers license type to "commercial drivers license (cdl)".`,
    uiPath: ['Vehicle', 'Driver', 'Driver', 'Driver License Type'],
    keyPath: ['vehic', vehicleIndex - 1, 'driver', 0, 'DL_TYPE'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: true,
  }));

  ruleIdValidationErrorFunctionMap.set('DriverLicenseNumberMustNotBeEmptyIfRequired', (vehicleIndex: number) => ({
    longDescription: `Please enter the driver license number for the driver of vehicle ${vehicleIndex}. It is required.`,
    uiPath: ['Vehicle', 'Driver', 'Driver', 'Driver License Number'],
    keyPath: ['vehic', vehicleIndex - 1, 'driver', 0, 'DR_LIC_NUM'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('TowStatusMustBeConsistentWithDamageExtentForDisablingDamage', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Reason Towed',
    longDescription: `Since vehicle ${vehicleIndex} has first vehicle damage extent of "disabling damage", set reason towed to "towed (disabling damanage)".`,
    uiPath: ['Vehicle', 'Vehicle', 'Vehicle', 'Reason Towed?'],
    keyPath: ['vehic', vehicleIndex - 1, 'vehic', 0, 'REAS_TOW_CD'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: true,
  }));

  // i'm commenting out this block because a rule id can ECrashLA.Core.Models.CrashReports.Validation.Rules.Passengers.AddressStateMustNotBeEmptyIfApplicable
  //    or ECrashLA.Core.Models.CrashReports.Validation.Rules.Driver.AddressStateMustNotBeEmptyIfApplicable and probably others
  //    the text is descriptive enough anyway

  // ruleIdValidationErrorFunctionMap.set('AddressStateMustNotBeEmptyIfApplicable', (vehicleIndex: number) => ({
  //   shortDescription: 'Invalid Driver Address',
  //   longDescription: `Please select the state for the address of the driver of vehicle ${vehicleIndex}. It is required.`,
  //   uiPath: ['Vehicle', 'Driver', 'Driver', "Driver's Address"],
  //   keyPath: ['vehic', vehicleIndex - 1, 'driver', 0, 'DR_STATE'],
  //   schemaGroupRepeats: true,
  //   schemaGroupIndex: vehicleIndex - 1,
  //   subGroupRepeats: false,
  //   subGroupIndex: 0,
  //   hasDownstreamValueChanges: false,
  // }));

  ruleIdValidationErrorFunctionMap.set('SeatingPositionShouldBeFrontRow', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Driver Seating Position',
    longDescription: `The driver of vehicle ${vehicleIndex} should be seated in the front row of the vehicle.`,
    uiPath: ['Vehicle', 'Factors', 'Driver Factors', 'Driver Seating Position'],
    keyPath: ['vehic', vehicleIndex - 1, 'contribfactors', 0, 'DR_POS_CD'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  /**
   * TODO: the message for this error contains too many numbers, we will need additional logic to extract the vehicle index, the current error message is understandable enough for the time being
   * "Vehicle 2's model year is 1995, but US cars were not required to have airbags until 1998.\r\nChoose "Not Applicable" if the driver of Vehicle 2 has no airbags."
   */
  // ruleIdValidationErrorFunctionMap.set('AirbagDeploymentShouldBeNotApplicableIfModelYearIsBefore1998', (vehicleIndex: number) => ({
  //   shortDescription: 'Invalid Airbag Deployment',
  //   longDescription: `Since the model year for vehicle ${vehicleIndex} is before 1998, the airbag deployment should be set to not applicable.`,
  //   uiPath: ['Vehicle', 'Factors', 'Driver Factors', 'Driver Air Bag Deployment'],
  //   keyPath: ['vehic', vehicleIndex - 1, 'contribfactors', 0, 'DR_AIRBAG_CD'],
  //   schemaGroupRepeats: true,
  //   schemaGroupIndex: vehicleIndex - 1,
  //   subGroupRepeats: false,
  //   subGroupIndex: 0,
  // }));

  /**
   * TODO: i don't know how users are triggering this error, the form control is required
   */
  ruleIdValidationErrorFunctionMap.set('DriverActionsMustNotBeEmpty', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Driver Actions',
    longDescription: `Please select the actions that the driver of vehicle ${vehicleIndex} performed at the time of the crash. It is required.`,
    uiPath: ['Vehicle', 'Factors', 'Driver Factors', 'Driver Action'],
    keyPath: ['vehic', vehicleIndex - 1, 'contribfactors', 0, 'VIOLATIONS_CD'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: true,
  }));

  /**
   * TODO: i don't know how users are triggering this error
   * if the user indicates that the tag is non-expiring, the registration year form control is set to null, hidden, and the back end is patched
   * if the user indicates that the tag is expiring, the registration year form control is optional which ecrash seems to be okay with
   */
  // ruleIdValidationErrorFunctionMap.set('TagYearMustBeNullIfNonExpiring', (vehicleIndex: number) => ({
  //   shortDescription: 'Invalid Tag Year',
  //   longDescription: `Since the tag for vehicle ${vehicleIndex} is non-expiring, the tag year should be set to null.`,
  //   uiPath: ['Vehicle', 'Vehicle', 'Vehicle', 'Tag Expiration Year'],
  //   keyPath: ['vehic', vehicleIndex - 1, 'vehic', 0, 'REG_YEAR'],
  //   schemaGroupRepeats: true,
  //   schemaGroupIndex: vehicleIndex - 1,
  //   subGroupRepeats: false,
  //   subGroupIndex: 0,
  //   hasDownstreamValueChanges: false,
  // }));

  ruleIdValidationErrorFunctionMap.set('CrashMannerMustBeNotACollisionIfFirstHarmfulEventDoesNotInvolveMotorVehicle', () => ({
    shortDescription: 'Invalid Crash Manner',
    longDescription:
      'Since the first harmful event does not involve a collision with a motor vehicle in transport, set the crash manner to "not a collision between 2 motor vehicles in transport". if the first harmful event does involve a collision with a motor vehicle in transport, set first harmful event to the appropriate event.',
    uiPath: ['Overview', 'Event Details', 'Event Details', 'Crash Manner'],
    keyPath: ['crash', 0, 'event', 0, 'MAN_COLL_CD'],
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('LicensePlateTypeMustBeOutOfStateIfTagStateIsNotLouisiana', (vehicleIndex: number) => ({
    shortDescription: 'Invalid License Plate Type',
    longDescription: `Since the tag state for vehicle ${vehicleIndex} is not Louisiana, the license plate type should be set to out-of-state license plate.`,
    uiPath: ['Vehicle', 'Vehicle', 'Vehicle', "Vehicle's Licence Plate Type"],
    keyPath: ['vehic', vehicleIndex - 1, 'vehic', 0, 'PLATE_TYPE'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('FirstHarmfulEventMustAppearInSequenceOfEventsForAVehicle', () => ({
    shortDescription: 'Invalid Incident First Harmful Event / Vehicle First Harmful Event',
    longDescription: 'The first harmful event for the incident must equal the first harmful event for one of the vehicles in the incident.',
    uiPath: ['Overview', 'Event Details', 'Event Details', 'First Harmful Event'],
    keyPath: ['crash', 0, 'event', 0, 'FIRST_HARMFUL_EVENT_CD'],
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: true,
  }));

  ruleIdValidationErrorFunctionMap.set('CrashSeverityMustMatchTheMostSevereInjuryStatus', () => ({
    shortDescription: 'Invalid Crash Severity / Driver Injury / Occupant Injury / Non-Motorist Injury',
    longDescription: 'The crash severity must match the injury status of all drivers, occupants, and non-motorists involved in the crash.',
    uiPath: ['Overview', 'Overview', 'Overview', 'Crash Severity'],
    keyPath: ['crash', 0, 'crash', 0, 'CRASH_SERVERITY_CD'],
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  /**
   * TODO: i'm not sure how users are getting the form in this state
   */
  ruleIdValidationErrorFunctionMap.set('DistractionActionMustNotBeNoIfConditionIsInattentive', (vehicleIndex: number) => ({
    longDescription: `You have indicated that a contributing action for ${vehicleIndex} is an inattentive or distracted driver. You have also indicated that the driver was not distracted. Please set Driver Distract to the "Distracted" option.`,
  }));

  ruleIdValidationErrorFunctionMap.set('VinShouldBeValid', (vehicleIndex: number) => ({
    shortDescription: 'Invalid VIN',
    longDescription: `The VIN you entered for vehicle ${vehicleIndex} is invalid. It should have 17 characters and contain letters and numbers only. It cannot contain the letters I, O, or Q.`,
    uiPath: ['Vehicle', 'Vehicle', 'Vehicle', 'Vehicle Identification Number'],
    keyPath: ['vehic', vehicleIndex - 1, 'vehic', 0, 'VIN'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('DriverLicenseStateMustNotBeEmptyIfRequired', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Driver License State',
    longDescription: `Please select the state that issued the driver license of the driver of vehicle ${vehicleIndex}. It is required.`,
    uiPath: ['Vehicle', 'Driver', 'Driver', 'Driver License State'],
    keyPath: ['vehic', vehicleIndex - 1, 'driver', 0, 'DR_LIC_STATE'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('ModelDescriptionMustBeNullIfNotRequired', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Model Description',
    longDescription: `Please clear the model description for vehicle ${vehicleIndex}. It is not required.`,
    uiPath: ['Vehicle', 'Vehicle', 'Vehicle', "Vehicle's Model Description"],
    keyPath: ['vehic', vehicleIndex - 1, 'vehic', 0, 'VEH_MODEL_DESC'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('IsOwnerNameSameAsDriverMustBeFalseIfDriverNameIsUnknown', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Owner Name Same as Driver',
    longDescription: `Since the name of the driver of vehicle ${vehicleIndex} is unknown, set is driver same as vehicle owner to "no" and is owner address same as driver to "no". Owner name and address can be empty.`,
    uiPath: ['Vehicle', 'Driver', 'Vehicle Owner', 'Is Driver Same As Vehicle Owner?'],
    keyPath: ['vehic', vehicleIndex - 1, 'driver', 0, 'OWNER_INFO_DR_SAME'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: true,
  }));
  ``;
  /**
   * TODO: you see this error when the error right above is triggered
   * by fixing the error above, this error goes away
   */
  ruleIdValidationErrorFunctionMap.set('OwnerNameMustMatchDriverIfIsOwnerNameSameAsDriverIsTrue', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Owner Name Same as Driver',
    longDescription: `If the name of the driver of vehicle ${vehicleIndex} is unknown, set is driver same as vehicle owner to "no" and is owner address same as driver to "no". Owner name and address can be empty. If the name of the driver is known, set is driver same as vehicle owner to "yes" if the owner name is the same as the driver name. If the driver is not the same as the owner but their address is the same, set is owner address same as driver to "yes".`,
    uiPath: ['Vehicle', 'Driver', 'Vehicle Owner', 'Is Driver Same As Vehicle Owner?'],
    keyPath: ['vehic', vehicleIndex - 1, 'driver', 0, 'OWNER_INFO_DR_SAME'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: true,
  }));

  ruleIdValidationErrorFunctionMap.set('EndorsementComplianceMustBeEndorsementsRequiredIfVehicleIsSchoolBus', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Drivers Endorsement Compliance',
    longDescription: `Since the vehicle ${vehicleIndex} is a school bus, driver's endorsement compliance should be set to "endorsements required and complied with", "not complied with", or "endorsements required".`,
    uiPath: ['Vehicle', 'Driver', 'Driver', "Driver's Endorsement Compliance"],
    keyPath: ['vehic', vehicleIndex - 1, 'driver', 0, 'DL_ENDORSEMENT_COMP_CD'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  /**
   * TODO: not sure how users are triggering this error as trafficway travel direction is required
   */
  ruleIdValidationErrorFunctionMap.set('TrafficwayTravelDirectionMustNotBeEmpty', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Trafficway Travel Direction',
    longDescription: `Please select the directions in which travel was permitted for the road on which vehicle ${vehicleIndex} was traveling. It is required.`,
    uiPath: ['Vehicle', 'Factors', 'Vehicle Factors', 'Trafficway Travel Direction'],
    keyPath: ['vehic', vehicleIndex - 1, 'contribfactors', 0, 'TRAFF_TRAVEL_DIR_CD'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  /**
   * TODO: not sure how users are triggering this error as trafficway division is required
   */
  ruleIdValidationErrorFunctionMap.set('TrafficwayDivisionMustNotBeEmpty', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Trafficway Division',
    longDescription: `You must indicate how the road on which vehicle ${vehicleIndex} was traveling was divided. It is required.`,
    uiPath: ['Vehicle', 'Factors', 'Vehicle Factors', 'Trafficway Division'],
    keyPath: ['vehic', vehicleIndex - 1, 'contribfactors', 0, 'TRAFF_DIVISION_CD'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  /**
   * TODO: not sure how users are triggering this error as roadway grade is required
   */
  ruleIdValidationErrorFunctionMap.set('RoadwayGradeMustNotBeEmpty', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Roadway Grade',
    longDescription: `You must select the grade of the roadway on which vehicle ${vehicleIndex} was traveling. It is required.`,
    uiPath: ['Vehicle', 'Factors', 'Vehicle Factors', 'Roadway Grade'],
    keyPath: ['vehic', vehicleIndex - 1, 'contribfactors', 0, 'VEH_ROADWAY_GRADE_CD'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  /**
   * TODO: not sure how users are triggering this error cause vehicle model description is always hidden
   * maybe they are selecting a model that requires a description but we are not providing one?
   */
  ruleIdValidationErrorFunctionMap.set('ModelDescriptionMustNotBeEmptyIfRequired', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Vehicle Model Description',
    longDescription: `You must describe the model of vehicle ${vehicleIndex}. It is required.`,
    uiPath: ['Vehicle', 'Vehicle', 'Vehicle', "Vehicle's Model Description"],
    keyPath: ['vehic', vehicleIndex - 1, 'vehic', 0, 'VEH_MODEL_DESC'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  /**
   * TODO: not sure how users are triggering this error cause event description is always hidden
   * maybe they are selecting an event that requires a description but we are not providing one?
   * also, we can't provide a path cause each event / event description is a separate form control, it's not repeatable
   */
  ruleIdValidationErrorFunctionMap.set('EventDescriptionMustNotBeEmptyIfRequired', (eventIndex: number, vehicleIndex: number) => ({
    shortDescription: 'Invalid Event Description',
    longDescription: `You must describe the event that occurred for event ${eventIndex} in the sequence of events for vehicle ${vehicleIndex}. It is required.`,
    uiPath: null,
    keyPath: null,
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('TrafficwayBarrierTypeMustBeNoBarrierIfTrafficwayDivisionIsNotDivided', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Trafficway Barrier Type',
    longDescription: `Since the trafficway division for vehicle ${vehicleIndex} is not divided, the barrier type should be set to no barrier.`,
    uiPath: ['Vehicle', 'Factors', 'Vehicle Factors', 'Trafficway Barrier Type'],
    keyPath: ['vehic', vehicleIndex - 1, 'contribfactors', 0, 'TRAFF_BARRIER_TYPE_CD'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  // not super worried about this one cause its in the commerical section
  ruleIdValidationErrorFunctionMap.set('MotorCarrierIdentificationNumberMustBeNullIfNotRequired', (vehicleIndex: number) => ({
    shortDescription: 'Invalid Motor Carrier Identification Number',
    longDescription: `Please clear the motor carrier identification number for vehicle ${vehicleIndex}. It is not required.`,
    uiPath: ['Vehicle', 'Vehicle', 'Commercial', 'Motor Carrier Identification'],
    keyPath: ['vehic', vehicleIndex - 1, 'vehic', 0, 'MTR_CARRIER_ID_TYPE'],
    schemaGroupRepeats: true,
    schemaGroupIndex: vehicleIndex - 1,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  /**
   * TODO: come back to this one, i can't recreate the error
   */
  ruleIdValidationErrorFunctionMap.set('TrafficControlDevicesPresentMustNotBeNoIfSequenceOfEventsInvolvesTrafficControlSupport', (vehicleIndex: number) => ({
    longDescription: `Since the sequence of events for vehicle ${vehicleIndex} includes a traffic control support, the traffic control devices present should not be set to no.`,
  }));

  ruleIdValidationErrorFunctionMap.set('JunctionLocationCannotBeAnIntersectionIfIntersectionApproachIsNotAnIntersection', () => ({
    shortDescription: 'Invalid Junction Location',
    longDescription: 'Since the incident did not occur at an intersection, the relation to junction should NOT be set to "intersection or related".',
    uiPath: ['Overview', 'Location', 'Roadway', 'Junction Location'],
    keyPath: ['crash', 0, 'location', 0, 'JUCTION_LOCATION_CD'],
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  /**
   * TODO: come back to this one, i can't recreate the error
   */
  ruleIdValidationErrorFunctionMap.set('OwnerNameMustBeNullIfNotRequired', (vehicleIndex: number) => ({
    longDescription: `Please clear the owner name for vehicle ${vehicleIndex}. It is not required.`,
  }));

  /**
   * TODO: come back to this one, i can't recreate the error
   */
  ruleIdValidationErrorFunctionMap.set('VehiclesShouldIncludeAtLeastOneLeftManeuverIfCrashMannerIsLeft', () => ({
    shortDescription: 'Invalid Crash Manner / Vehicle Maneuver',
    longDescription: "Since the manner of crash is angle - left into flow, at least one vehicle's maneuver should be turning left or making U-turn.",
    uiPath: null,
    keyPath: null,
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  ruleIdValidationErrorFunctionMap.set('CrashMannerMustNotBeNotACollisionIfFirstHarmfulEventInvolvesMotorVehicle', () => ({
    shortDescription: 'Invalid Crash Manner',
    longDescription:
      "Since the first harmful event involves a collision with a motor vehicle in transport, the crash manner must NOT be 'not a collision between two motor vehicles in transport'. If the incident involved a parked vehicle or non-vehicular object, change the first harmful event to the appropriate event.",
    uiPath: ['Overview', 'Event Details', 'Event Details', 'Crash Manner'],
    keyPath: ['crash', 0, 'event', 0, 'MAN_COLL_CD'],
    schemaGroupRepeats: false,
    schemaGroupIndex: 0,
    subGroupRepeats: false,
    subGroupIndex: 0,
    hasDownstreamValueChanges: false,
  }));

  return ruleIdValidationErrorFunctionMap;
};
